import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useSidebar } from "../../contexts/sidebar-context";
import { AlertCircleIcon, BookUser, Building, ChevronDownIcon, Copyright, FileChartColumnIncreasing, History, Home, LaptopIcon, Loader, Users } from "lucide-react";
import routePaths from "../../global/routePath";
import useIsActivePath from "../../hooks/useIsActivePath";
import { usePermissions } from "../../hooks/usePermissions";
import logo from '../../../public/icons/round-logo.svg';

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean; isActive?: boolean }[];
  isActive?: boolean;
};


const AppSidebar: React.FC = () => {
  const { canView } = usePermissions();
  const dashboardIsActive = useIsActivePath(routePaths.home.base);
  const institutionIsActive = useIsActivePath(`${routePaths.institution.base}*`);
  const screenIsActive = useIsActivePath(`${routePaths.screen.base}*`);
  const screenFrameConfigIsActive = useIsActivePath(`screen-frame-configs/*`);
  const frameIsActive = useIsActivePath(`${routePaths.frames.base}*`);
  const alertIsActive = useIsActivePath(`${routePaths.alerts.base}*`);
  const playbackHistoryIsActive = useIsActivePath(`${routePaths.playbackHistory.base}*`);
  const contentLibraryIsActive = useIsActivePath(`${routePaths.contentLibrary.base}*`);
  const approvalPendingIsActive = useIsActivePath(`${routePaths.componentContent.approvalPendings}*`);
  const userIsActive = useIsActivePath(`${routePaths.users.base}*`);

  const navItems: NavItem[] = [
    {
      icon: <Home />,
      name: "Dashboard",
      path: "/",
      isActive: dashboardIsActive,
    },
    canView('institutions') && {
      icon: <Building />,
      name: "Institutions",
      path: routePaths.institution.categories,
      isActive: institutionIsActive,
    },
    canView('screens') && {
      icon: <LaptopIcon />,
      name: "Screens",
      path: routePaths.screen.categories,
      isActive: screenIsActive || screenFrameConfigIsActive,
    },
    canView('frames') && {
      icon: <FileChartColumnIncreasing />,
      name: "Frames",
      path: routePaths.frames.categories,
      isActive: frameIsActive,
    },
    canView('alerts') && {
      icon: <AlertCircleIcon />,
      name: "Alerts",
      path: routePaths.alerts.base,
      isActive: alertIsActive,
    },
    canView('playbackHistory') && {
      icon: <History />,
      name: "Playback History",
      path: routePaths.playbackHistory.base,
      isActive: playbackHistoryIsActive,
    },
    canView('contentLibrary') && {
      icon: <BookUser />,
      name: "Content Library",
      path: routePaths.contentLibrary.base,
      isActive: contentLibraryIsActive,
    },
    canView('alerts') &&
    {
      icon: <Loader />,
      name: "Approval Pendings",
      path: routePaths.componentContent.approvalPendings,
      isActive: approvalPendingIsActive,
    },
    canView('users') && {
      icon: <Users />,
      name: "Users",
      path: routePaths.users.base,
      isActive: userIsActive,
    },
  ].filter(Boolean) as NavItem[];
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  // const location = useLocation();

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className="flex flex-col gap-2">
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group w-full text-left rounded-lg transition-all duration-200 ${openSubmenu?.type === menuType && openSubmenu?.index === index
                ? "bg-destructive text-white"
                : "text-white/80 hover:bg-destructive hover:text-white"
                } ${!isExpanded && !isHovered
                  ? "lg:justify-center px-3 py-3"
                  : "lg:justify-start px-4 py-3"
                }`}
            >
              <div className="flex items-center gap-3">
                <span className="flex-shrink-0 w-5 h-5">
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <>
                    <span className="flex-1 font-medium text-sm">{nav.name}</span>
                    <ChevronDownIcon
                      className={`w-4 h-4 transition-transform duration-200 ${openSubmenu?.type === menuType &&
                        openSubmenu?.index === index
                        ? "rotate-180"
                        : ""
                        }`}
                    />
                  </>
                )}
              </div>
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                className={`menu-item group flex items-center gap-3 rounded-lg transition-all duration-200 ${nav.isActive
                  ? "bg-destructive text-white shadow-sm"
                  : "text-white/80 hover:bg-destructive hover:text-white"
                  } ${!isExpanded && !isHovered
                    ? "lg:justify-center px-3 py-3"
                    : "lg:justify-start px-4 py-3"
                  }`}
              >
                <span className="flex-shrink-0 w-5 h-5">
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="font-medium text-sm">{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-1 space-y-1 ml-11 mr-2">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      to={subItem.path}
                      className={`block px-3 py-2 rounded-md text-sm transition-colors ${subItem.isActive
                        ? "bg-destructive text-white"
                        : "text-white/70 hover:bg-destructive hover:text-white"
                        }`}
                    >
                      {subItem.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <aside
      className={`fixed top-0 left-0 h-[calc(100vh)] bg-primary transition-all duration-300 ease-in-out z-40 overflow-hidden
        ${isExpanded || isMobileOpen
          ? "w-[220px]"
          : isHovered
            ? "w-[220px]"
            : "w-[70px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo Section */}
      <div
        className={`py-3.5 flex ${!isExpanded && !isHovered ? "lg:justify-center" : "justify-center"
          }`}
      >
        <Link className="flex justify-center w-full px-3" to="/">
          {(isExpanded || isHovered || isMobileOpen) ? (
            <div className="text-white flex justify-center items-center gap-1.5 font-semibold text-sm">
              <img src={logo} className="h-8 bg-white rounded-full" alt="logo" />
              <span className="flex-nowrap flex text-[12px] ">Creative Signage Nepal</span>
            </div>
          ) : (
            <div className="text-white font-bold text-lg">
              <img src={logo} className="h-8 bg-white rounded-full" alt="logo" />
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <div className="flex flex-col overflow-y-auto h-[calc(100%-4.5rem)] custom-scrollbar px-2">
        <nav className="pb-4">
          <div className="flex flex-col gap-1.5">
            {renderMenuItems(navItems, "main")}
          </div>
        </nav>
      </div>
      <div className={`fixed flex justify-center bottom-6 transition-all duration-300 
      ${
        isExpanded || isHovered || isMobileOpen ? 'w-[220px]' : 'w-[70px]'
      }
      `}>
        <div className='flex p-1.5 border rounded shadow border-border text-white justify-center items-center text-[11px]'>
          <Copyright className={`${isExpanded || isHovered || isMobileOpen ? 'mr-1.5' : 'mr-0'}`} size={14} />
          {(isExpanded || isHovered || isMobileOpen) && (
            <span className="whitespace-nowrap text-[11px]">{new Date().getFullYear()} Creative Signage Nepal</span>
          )}
        </div>
      </div>
    </aside>
  );
};

export default AppSidebar;
